import moment from 'moment';
import type {
  CreateAnchorBackendPayload,
  InsightBackendPayload,
  UpdateAnchorBackendPayload,
  UpdateAnomalyBackendPayload,
} from '../../../Insights/types';
import type { AnomalyBandWithForm } from './AnomalyBand/types';
import { enMessages } from '../../../../../../../messages/en-US';

export type OptionItem = {
  text: string;
  value: string;
};

/** What an anomaly is attached to; decides which streams can be ticked. */
export type AnomalyTargetKind = 'point' | 'device';

type StreamDefinition = {
  value: string;
  /** i18n key, resolved against enMessages.custom for the fallback. */
  key: keyof typeof enMessages.custom;
  /**
   * Short code that marks the stream in lists. Fixed and unique per stream rather than derived
   * from the label: initials of a translated label collide (Cellular RSSI / RSRP / RSRQ all give
   * "CR") and change with the language. Max three characters — the badge is 20px wide.
   */
  badge: string;
};

const ENDPOINT_STREAMS: StreamDefinition[] = [
  { value: 'flow', key: 'flow', badge: 'FLW' },
  { value: 'cumulative_flow', key: 'cumulativeFlow', badge: 'CFL' },
  { value: 'pressure', key: 'pressure', badge: 'PRS' },
  { value: 'gateway_rssi', key: 'gatewayRssi', badge: 'GRS' },
  { value: 'endpoint_uptime', key: 'endpointUptime', badge: 'EUP' },
  { value: 'cpu_temperature', key: 'cpuTemperature', badge: 'CPU' },
  { value: 'fuel_gauge_temperature', key: 'fuelGaugeTemperature', badge: 'FGT' },
  { value: 'voltage', key: 'voltageTooltip', badge: 'VLT' },
  { value: 'battery_charge', key: 'batteryCharge', badge: 'BAT' },
  { value: 'battery_slot', key: 'batterySlot', badge: 'SLT' },
  { value: 'sync_state', key: 'syncState', badge: 'SYN' },
];

const GATEWAY_STREAMS: StreamDefinition[] = [
  { value: 'gateway_info', key: 'gatewayInfo', badge: 'INF' },
  { value: 'gateway_uptime', key: 'gatewayUptime', badge: 'GUP' },
  { value: 'gateway_temperature', key: 'gatewayTemperature', badge: 'GTP' },
  { value: 'gateway_disk', key: 'gatewayDisk', badge: 'DSK' },
  { value: 'gateway_logs', key: 'gatewayLogs', badge: 'LOG' },
  { value: 'pump_state', key: 'pumpState', badge: 'PST' },
  { value: 'cellular_rssi', key: 'cellularRssi', badge: 'RSS' },
  { value: 'cellular_rsrp', key: 'cellularRsrp', badge: 'RSP' },
  { value: 'cellular_rsrq', key: 'cellularRsrq', badge: 'RSQ' },
  { value: 'cellular_sinr', key: 'cellularSinr', badge: 'SNR' },
  { value: 'cellular_network', key: 'cellularNetwork', badge: 'NET' },
];

/**
 * Values stored before the vocabulary was split per target type. Never offered for new records,
 * only kept so existing ones still render a label instead of the raw value.
 */
const LEGACY_STREAMS: StreamDefinition[] = [
  { value: 'temperature', key: 'temperature', badge: 'TMP' },
  { value: 'pump', key: 'pump', badge: 'PMP' },
];

const ALL_STREAMS = [...ENDPOINT_STREAMS, ...GATEWAY_STREAMS, ...LEGACY_STREAMS];

const STREAM_BADGE_BY_VALUE = new Map(ALL_STREAMS.map((stream) => [stream.value, stream.badge]));

/** Short code for a stream; legacy values resolve to their current stream first. */
export const getStreamBadge = (value: string): string | undefined =>
  STREAM_BADGE_BY_VALUE.get(normalizeStreamValue(value));

export const STREAM_OPTIONS: OptionItem[] = ALL_STREAMS.map((stream) => ({
  text: enMessages.custom[stream.key],
  value: stream.value,
}));

export const CRITICALITY_OPTIONS: OptionItem[] = [
  { text: 'Low', value: 'Low' },
  { text: 'Medium', value: 'Medium' },
  { text: 'High', value: 'High' },
  { text: 'Critical', value: 'Critical' },
];

type LocalizationServiceLike = {
  toLanguageString: (key: string, defaultValue: string) => string;
};

/**
 * Streams that can be ticked for one target type. Called with no target kind it returns every stream,
 * which is what the lists and filters need in order to label any stored value.
 */
export const getLocalizedStreamOptions = (
  localizationService: LocalizationServiceLike,
  targetKind?: AnomalyTargetKind
): OptionItem[] => {
  const streams =
    targetKind === 'device' ? GATEWAY_STREAMS : targetKind === 'point' ? ENDPOINT_STREAMS : ALL_STREAMS;

  return streams.map((stream) => ({
    text: localizationService.toLanguageString(`custom.${stream.key}`, enMessages.custom[stream.key]),
    value: stream.value,
  }));
};

export const getLocalizedCriticalityOptions = (localizationService: LocalizationServiceLike): OptionItem[] => [
  { text: localizationService.toLanguageString('custom.low', enMessages.custom.low), value: 'Low' },
  { text: localizationService.toLanguageString('custom.medium', enMessages.custom.medium), value: 'Medium' },
  { text: localizationService.toLanguageString('custom.high', enMessages.custom.high), value: 'High' },
  { text: localizationService.toLanguageString('custom.critical', enMessages.custom.critical), value: 'Critical' },
];

const CRITICALITY_ALIASES: Record<string, string> = {
  Minor: 'Low',
  Moderate: 'Medium',
  Major: 'High',
  Low: 'Low',
  Medium: 'Medium',
  High: 'High',
  Critical: 'Critical',
};

export const normalizeCriticality = (value: string | undefined): string | undefined =>
  value ? CRITICALITY_ALIASES[value] ?? value : undefined;

const STREAM_VALUE_BY_TEXT = new Map<string, string>(
  STREAM_OPTIONS.map((opt) => [opt.text.toLowerCase(), opt.value])
);
const STREAM_VALID_VALUES = new Set(STREAM_OPTIONS.map((opt) => opt.value));

/**
 * Stored documents are never rewritten; the old vocabulary is translated as records are read.
 * `temperature` and `pump` are deliberately absent: an old record cannot say which of the two
 * new temperatures it meant, and `pump` has no endpoint equivalent, so both keep their own label.
 */
const LEGACY_STREAM_VALUES: Record<string, string> = {
  volumeDispensed: 'flow',
  rssi: 'gateway_rssi',
};

export const normalizeStreamValue = (value: string): string => {
  if (!value) return value;
  const migrated = LEGACY_STREAM_VALUES[value];
  if (migrated) return migrated;
  if (STREAM_VALID_VALUES.has(value)) return value;
  return STREAM_VALUE_BY_TEXT.get(value.toLowerCase()) ?? value;
};

export const normalizeStreamValues = (values: string[] | undefined): string[] =>
  Array.isArray(values) ? values.map(normalizeStreamValue) : [];

export const ML_AUTHOR_NAME = 'greaseboss ml';

export const isMlAuthor = (user: string | undefined | null): boolean =>
  !!user && user.trim().toLowerCase() === ML_AUTHOR_NAME;

export const isMlAnomaly = (anomalyId: string | undefined | null): boolean =>
  !!anomalyId && anomalyId.trim().length > 0;

export const CONFIDENCE_OPTIONS = Array.from({ length: 10 }, (_, i) => ({
  text: String(i + 1),
  value: i + 1,
}));

export const FIELD_SPACING = '0.05rem';

export const streamsRequiredValidator = (value: unknown): string | undefined =>
  Array.isArray(value) && value.length > 0 ? undefined : enMessages.custom.fieldIsRequired;

export const criticalityRequiredValidator = (value: unknown): string | undefined =>
  value != null && value !== '' ? undefined : enMessages.custom.fieldIsRequired;

const isValidDate = (value: unknown): value is Date =>
  value instanceof Date && !Number.isNaN(value.getTime());

// Anomalies describe what already happened — no part of the period may be in the future.
export const isFutureDate = (value: Date): boolean => value.getTime() > Date.now();

export const startDateTimeValidator = (
  value: unknown,
  valueGetter: (name: string) => unknown
): string | undefined => {
  if (!isValidDate(value)) {
    return enMessages.custom.fieldIsRequired;
  }

  const endDateTime = valueGetter('endDateTime');
  if (isValidDate(endDateTime) && value.getTime() > endDateTime.getTime()) {
    return enMessages.custom.startMustBeBeforeEnd;
  }

  return undefined;
};

export const endDateTimeValidator = (
  value: unknown,
  valueGetter: (name: string) => unknown
): string | undefined => {
  if (!isValidDate(value)) {
    return enMessages.custom.fieldIsRequired;
  }

  const startDateTime = valueGetter('startDateTime');
  if (isValidDate(startDateTime) && value.getTime() < startDateTime.getTime()) {
    return enMessages.custom.endMustBeAfterStart;
  }

  return undefined;
};

export const toUnixSeconds = (value: Date): number => Math.floor(value.getTime() / 1000);

/**
 * An anomaly is "ongoing" (open-ended) when it has no end date. The backend receives
 * `null` as `endDate`; on read, a missing/null/unparseable end date — or the legacy
 * year-9999 sentinel — means ongoing.
 */
export const isOngoingEndDate = (value: string | number | Date | null | undefined): boolean => {
  if (value == null || value === '') return true;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return true;
  return date.getUTCFullYear() >= 9999;
};

export const toComboValue = (v: unknown): string | undefined => {
  if (v == null) return undefined;
  if (typeof v === 'string') return v;
  if (typeof v === 'object' && v !== null && 'value' in v) return (v as { value?: string }).value;
  if (typeof v === 'object' && v !== null && 'text' in v) return (v as { text?: string }).text;
  return undefined;
};

export const toMultiValues = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => {
      if (typeof item === 'string') {
        return item;
      }

      if (item && typeof item === 'object') {
        if ('value' in item && typeof (item as { value?: unknown }).value === 'string') {
          return (item as { value: string }).value;
        }

        if ('text' in item && typeof (item as { text?: unknown }).text === 'string') {
          return (item as { text: string }).text;
        }
      }

      return '';
    })
    .filter((item): item is string => item.trim().length > 0);
};

export const toOptions = (values: string[]): OptionItem[] =>
  values.map((value) => ({ text: value, value }));

const toOption = (value: string): OptionItem => ({ text: value, value });

export const mergeOptions = (
  current: OptionItem[],
  incoming: OptionItem[]
): OptionItem[] => {
  const merged = [...current];

  incoming.forEach((item) => {
    if (!merged.some((existing) => existing.value === item.value)) {
      merged.push(item);
    }
  });

  return merged;
};

export const ensureOptions = (
  current: OptionItem[],
  values: string[] | undefined
): OptionItem[] => {
  if (!Array.isArray(values) || values.length === 0) {
    return current;
  }

  return values.reduce<OptionItem[]>((acc, value) => {
    if (!value || acc.some((item) => item.value === value)) {
      return acc;
    }

    return [...acc, toOption(value)];
  }, current);
};

export const formatNoteDate = (timestamp: string): string => {
  const date = moment(timestamp);
  return date.isValid() ? date.format('DD MMM YYYY HH:mm') : timestamp;
};

const READ_ONLY_FALLBACK = '-';

export const toReadOnlyValue = (value: string | number | undefined | null): string =>
  value == null || value === '' ? READ_ONLY_FALLBACK : String(value);

interface GetInitialValuesArgs {
  selectedBand: AnomalyBandWithForm | undefined;
  criticalityOptions: OptionItem[];
  indicatorOptions: OptionItem[];
  rootCauseOptions: OptionItem[];
  actionOptions: OptionItem[];
}

export const getInitialValuesFromBand = ({
  selectedBand,
  criticalityOptions,
  indicatorOptions,
  rootCauseOptions,
  actionOptions,
}: GetInitialValuesArgs) => {
  if (!selectedBand) {
    return {
      startDateTime: undefined,
      endDateTime: new Date(),
      streams: [],
      criticality: undefined,
      indicators: [],
      rootCauses: [],
      actions: [],
      confidence: undefined,
      savings: undefined,
      userNotes: '',
      ongoing: true,
    };
  }

  const streams = selectedBand.streams ?? [];
  const streamItems =
    streams.length > 0
      ? streams.map(
          (value) => STREAM_OPTIONS.find((option) => option.value === value) ?? { text: value, value }
        )
      : [];

  const ongoing = selectedBand.ongoing ?? true;

  return {
    startDateTime: new Date(selectedBand.startTime * 1000),
    // Ongoing anomalies have no end date — default the field to the current date/time.
    endDateTime: ongoing ? new Date() : new Date(selectedBand.endTime * 1000),
    streams: streamItems,
    criticality: normalizeCriticality(selectedBand.criticality)
      ? criticalityOptions.find((option) => option.value === normalizeCriticality(selectedBand.criticality)) ??
        toOption(normalizeCriticality(selectedBand.criticality)!)
      : undefined,
    indicators: (selectedBand.indicators ?? []).map(
      (value) => indicatorOptions.find((option) => option.value === value) ?? toOption(value)
    ),
    rootCauses: (selectedBand.rootCauses ?? []).map(
      (value) => rootCauseOptions.find((option) => option.value === value) ?? toOption(value)
    ),
    actions: (selectedBand.actions ?? []).map(
      (value) => actionOptions.find((option) => option.value === value) ?? toOption(value)
    ),
    confidence: CONFIDENCE_OPTIONS.find((option) => option.value === selectedBand.confidence) ?? undefined,
    savings: selectedBand.savings ?? undefined,
    userNotes: selectedBand.userNotes ?? '',
    ongoing,
  };
};

interface BuildAnomalyPayloadArgs {
  band: AnomalyBandWithForm;
  assetId: string;
  endpointId: string;
  /** Set when the record belongs to a device (e.g. gateway) instead of a point. */
  deviceId?: string;
  userName?: string;
  userId?: string;
}

export const buildAnomalyPayload = ({
  band,
  assetId,
  endpointId,
  deviceId,
  userName,
  userId,
}: BuildAnomalyPayloadArgs): InsightBackendPayload => {
  const authorName = band.createdByUserName ?? userName ?? '';
  const authorId = band.createdByUserId ?? userId ?? '';
  const noteComment = band.userNotes?.trim() ?? '';
  const existingNotes = band.notes ?? [];
  const pendingNotes = band.pendingNotes ?? [];
  const notes =
    noteComment.length > 0
      ? [
          ...existingNotes,
          ...pendingNotes,
          {
            timestamp: new Date().toISOString(),
            user: userName ?? '',
            comment: noteComment,
          },
        ]
      : [...existingNotes, ...pendingNotes];

  return {
    machineId: assetId,
    pointId: deviceId ? '' : endpointId,
    deviceId: deviceId || undefined,
    user: authorName,
    userId: authorId,
    startDate: new Date(band.startTime * 1000).toISOString(),
    endDate: band.ongoing ? null : new Date(band.endTime * 1000).toISOString(),
    anomalyId: band.anomalyId ?? '',
    streams: band.streams ?? [],
    form: {
      criticality: normalizeCriticality(band.criticality) ?? '',
      indicators: band.indicators ?? [],
      rootCauses: band.rootCauses ?? [],
      actions: band.actions ?? [],
      confidence: band.confidence ?? 0,
      estimatedSavings: band.savings ?? 0,
    },
    notes,
    revisionHistory: [],
    mlResults: band.mlResults ?? [],
  };
};

export const buildUpdateAnomalyPayload = ({
  band,
}: BuildAnomalyPayloadArgs): UpdateAnomalyBackendPayload => ({
  startDate: new Date(band.startTime * 1000).toISOString(),
  endDate: band.ongoing ? null : new Date(band.endTime * 1000).toISOString(),
  streams: band.streams ?? [],
  form: {
    criticality: normalizeCriticality(band.criticality) ?? '',
    indicators: band.indicators ?? [],
    rootCauses: band.rootCauses ?? [],
    actions: band.actions ?? [],
    confidence: band.confidence ?? 0,
    estimatedSavings: band.savings ?? 0,
  },
});

const buildMergedNotes = (band: AnomalyBandWithForm, userName?: string) => {
  const noteComment = band.userNotes?.trim() ?? '';
  const existingNotes = band.notes ?? [];
  const pendingNotes = band.pendingNotes ?? [];

  return noteComment.length > 0
    ? [
        ...existingNotes,
        ...pendingNotes,
        {
          timestamp: new Date().toISOString(),
          user: userName ?? '',
          comment: noteComment,
        },
      ]
    : [...existingNotes, ...pendingNotes];
};

export const buildCreateAnchorPayload = ({
  band,
  assetId,
  endpointId,
  deviceId,
  userName,
  userId,
}: BuildAnomalyPayloadArgs): CreateAnchorBackendPayload => {
  const authorName = band.createdByUserName ?? userName ?? '';
  const authorId = band.createdByUserId ?? userId ?? '';
  const notes = buildMergedNotes(band, userName);

  return {
    machineId: assetId,
    pointId: deviceId ? '' : endpointId,
    deviceId: deviceId || undefined,
    user: authorName,
    userId: authorId,
    startDate: new Date(band.startTime * 1000).toISOString(),
    endDate: new Date(band.endTime * 1000).toISOString(),
    streams: band.streams ?? [],
    notes,
    revisionHistory: band.revisionHistory ?? [],
  };
};

export const buildUpdateAnchorPayload = ({
  band,
  endpointId: _endpointId,
  assetId: _assetId,
  userName: _userName,
  userId: _userId,
}: BuildAnomalyPayloadArgs): UpdateAnchorBackendPayload => {
  return {
    startDate: new Date(band.startTime * 1000).toISOString(),
    endDate: new Date(band.endTime * 1000).toISOString(),
    streams: band.streams ?? [],
  };
};
